import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getExam } from '../api/exams';
import { useAuth } from '../context/AuthContext';
import ReadingOnboarding from './reading/ReadingOnboarding';
import ReadingHome from './reading/ReadingHome';
import Part1Screen from './reading/Part1Screen';
import Part2Screen from './reading/Part2Screen';
import Part3Screen from './reading/Part3Screen';
import Part4Screen from './reading/Part4Screen';
import Part5Screen from './reading/Part5Screen';
import ExamModeScreen from './reading/ExamModeScreen';

// ITS reading-experience wrapper. Mounts at /reading/:examId and uses
// the `?mode=` query string to switch between the multi-step flow
// (onboarding → home → part1..5 → exam → report) — same content the
// FET app shows at pass-fet.com, just served from the ITS routes.
//
// Designed to be a thin wrapper: each child screen owns its own state,
// progress, and submission logic. We're only responsible for:
//   • Loading the exam from the URL examId
//   • Picking which child to render based on `?mode=`
//   • Wiring the navigate / setToast / accountControl props the
//     children expect (because they were originally written against
//     the FET app's experience component).

export default function ReadingExperience() {
  const navigate = useNavigate();
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [exam, setExam] = useState(null);
  const [examLoading, setExamLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = width < 760;
  const mode = searchParams.get('mode') || 'reading-home';

  // Load the exam once on mount. We don't poll or refetch — the
  // reading content doesn't change mid-attempt.
  useEffect(() => {
    if (!examId) return;
    let cancelled = false;
    setExamLoading(true);
    getExam(examId)
      .then((data) => { if (!cancelled) setExam(data); })
      .catch((err) => { console.error(err); if (!cancelled) setExam(null); })
      .finally(() => { if (!cancelled) setExamLoading(false); });
    return () => { cancelled = true; };
  }, [examId]);

  // Auto-show the onboarding walkthrough the first time the user
  // enters the reading flow (matches FET's UX). After that the user
  // jumps straight to reading-home on subsequent visits.
  useEffect(() => {
    if (!user || authLoading) return;
    if (!examId) return;
    if (mode !== 'reading-home') return;
    try {
      const seen = window.localStorage.getItem('fet_reading_onboarded');
      if (!seen) navigate(`/reading/${examId}?mode=reading-onboarding`, { replace: true });
    } catch { /* private mode */ }
  }, [user, authLoading, examId, mode, navigate]);

  // Help children build URLs back to other modes of THIS exam. The
  // children received `navigate` originally; we just augment them with
  // a stable href builder so they don't need to know the route shape.
  const goToPart = useCallback((partNumber) => {
    navigate(`/reading/${examId}?mode=reading-part-${partNumber}`);
  }, [examId, navigate]);

  const goToHome = useCallback(() => {
    navigate(`/reading/${examId}?mode=reading-home`);
  }, [examId, navigate]);

  const goToExamMode = useCallback(() => {
    navigate(`/reading/${examId}?mode=reading-exam`);
  }, [examId, navigate]);

  // Compute the "next part with content" so each part screen can
  // chain into the next one. Falls back to "back to dashboard" when
  // the current part is the last one configured.
  const buildNextPartProps = useCallback((currentPartNumber) => {
    const allParts = exam?.reading_parts || [];
    const sorted = allParts
      .filter((p) => p && p.has_content && Number.isFinite(p.part_number))
      .sort((a, b) => a.part_number - b.part_number);
    const next = sorted.find((p) => p.part_number > currentPartNumber);
    if (!next) return {};
    return {
      nextPartLabel: `Continue to Part ${next.part_number} →`,
      onContinueToNextPart: () => goToPart(next.part_number),
    };
  }, [exam, goToPart]);

  const accountControl = useMemo(() => null, []);
  const theme = useMemo(() => ({
    // Minimal theme so the reading screens don't crash when reading
    // `theme.X`. They have their own _theme.js for visuals anyway.
    body: '#475569', muted: '#94a3b8', text: '#0f172a',
    border: '#e2e8f0', surface: '#fff', surfaceSoft: '#f8fafc',
    pageBg: '#fdfaef', pageBgSoft: '#fffaf2', shadow: '0 1px 2px rgba(15,23,42,.04)',
    accent: '#4f46e5',
  }), []);

  if (authLoading) return null;
  if (!user) {
    navigate(`/login?next=${encodeURIComponent(`/reading/${examId || ''}`)}`, { replace: true });
    return null;
  }

  if (mode === 'reading-onboarding') {
    return <ReadingOnboarding theme={theme} isMobile={isMobile} navigate={navigate} />;
  }

  if (mode === 'reading-exam') {
    return (
      <ExamModeScreen
        theme={theme}
        isMobile={isMobile}
        navigate={navigate}
        accountControl={accountControl}
        exam={exam}
        loading={examLoading}
        setToast={setToast}
      />
    );
  }

  // Part screens
  const PartByMode = {
    'reading-part-1': Part1Screen,
    'reading-part-2': Part2Screen,
    'reading-part-3': Part3Screen,
    'reading-part-4': Part4Screen,
    'reading-part-5': Part5Screen,
  };
  const PartCmp = PartByMode[mode];
  if (PartCmp) {
    const partNumber = Number(mode.split('-').pop());
    return (
      <>
        <PartCmp
          theme={theme}
          isMobile={isMobile}
          navigate={navigate}
          accountControl={accountControl}
          exam={exam}
          loading={examLoading}
          setToast={setToast}
          {...buildNextPartProps(partNumber)}
        />
        {toast && <Toast message={toast} onClose={() => setToast('')} />}
      </>
    );
  }

  // Default: reading-home
  return (
    <>
      <ReadingHome
        theme={theme}
        isMobile={isMobile}
        navigate={navigate}
        accountControl={accountControl}
        exam={exam}
        loading={examLoading}
        setToast={setToast}
        onOpenExamMode={goToExamMode}
        onOpenPart={(part) => {
          if (!part || !Number.isFinite(part.part_number)) return;
          if (part.part_number >= 1 && part.part_number <= 5) {
            goToPart(part.part_number);
          } else {
            setToast(`Part ${part.part_number} — coming in the next update.`);
          }
        }}
      />
      {toast && <Toast message={toast} onClose={() => setToast('')} />}
    </>
  );
}

function Toast({ message, onClose }) {
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [message, onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: '#0f172a', color: '#fff', padding: '10px 16px',
      borderRadius: 12, fontSize: 13, fontWeight: 700,
      boxShadow: '0 16px 36px rgba(15,23,42,.18)', zIndex: 50,
      fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>{message}</div>
  );
}
